import { Capacitor } from '@capacitor/core';
import { ArrowRight, ArrowSquareOut, MapPin } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import type { ChatBlock } from '@/slices/chat/chatSlice';
import type { ChatNavigationBlockPayload } from '@/types/chatBlocks';
import { resolveChatBlockTarget } from './chatBlockTargets';

interface NavigationBlockProps {
  block: ChatBlock;
  payload: ChatNavigationBlockPayload;
}

/** A named-route handoff. The server supplies a closed route id, never a URL. */
const NavigationBlock = ({ block, payload }: NavigationBlockProps) => {
  const isNative = Capacitor.isNativePlatform();
  const target = resolveChatBlockTarget(block.target);

  if (!target) {
    return (
      <p className="m-0 text-sm text-fg-secondary">
        This destination is unavailable right now.
      </p>
    );
  }

  return (
    <div className="flex items-center gap-3 p-3">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-surface-muted text-fg-secondary">
        <MapPin size={16} aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="!m-0 truncate font-heading text-sm font-medium text-fg-primary">
          {payload.label}
        </p>
        {payload.description ? (
          <p className="!m-0 !mt-0.5 line-clamp-2 text-xs leading-5 text-fg-secondary">
            {payload.description}
          </p>
        ) : null}
      </div>
      <Link
        to={target.to}
        target={isNative ? undefined : '_blank'}
        rel="noreferrer"
        aria-label={`Open ${payload.label}`}
        className="inline-flex min-h-9 shrink-0 items-center gap-1.5 rounded-lg border border-border-subtle bg-surface-muted px-2.5 text-xs font-medium text-fg-primary transition-colors hover:bg-surface-highlight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-default"
      >
        Open
        {isNative ? (
          <ArrowRight size={14} aria-hidden="true" />
        ) : (
          <ArrowSquareOut size={14} aria-hidden="true" />
        )}
      </Link>
    </div>
  );
};

export default NavigationBlock;
